import type { stacks } from "@dropbox/api-v2-client";
import { Popover } from "@dropbox/dig-components/popover";
import { Tabs } from "@dropbox/dig-components/tabs";
import { PAP_Cancel_UpdateStackEmoji } from "@mirage/analytics/events/types/cancel_update_stack_emoji";
import i18n from "@mirage/translations";
import { atom, useAtom } from "jotai";
import type { EmojiData } from "../FullScreenStack/EmojiPicker";
import { useReportStackPapEvent } from "../hooks/useReportStackPapEvent";
import { EmojiPicker } from "./EmojiPicker";
import { StackUploadPanel } from "./StackUploadPanel";

const selectedTabAtom = atom<"emoji" | "upload">("emoji");

export const StackIconPopover = ({
  isOpen,
  onEmojiSelect,
  anchorRef,
  onClose,
  updateStackLogo,
  isUploading,
  stack,
}: {
  isOpen: boolean;
  onEmojiSelect: (emoji: EmojiData) => void;
  anchorRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  updateStackLogo: (file: File) => void;
  isUploading: boolean;
  stack: stacks.Stack;
}) => {
  const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom);
  const reportStackPapEvent = useReportStackPapEvent(stack);

  return (
    <Popover
      open={isOpen}
      closeOnOutsideClick
      onToggle={(open: boolean) => {
        if (!open) {
          if (selectedTab === "emoji") {
            reportStackPapEvent(PAP_Cancel_UpdateStackEmoji());
          }
          onClose();
        }
      }}
      anchorEl={anchorRef.current}
      placement="right-start"
    >
      <Popover.Overlay>
        <Popover.Content>
          <Popover.Body>
            <Tabs
              selectedTab={selectedTab}
              onSelection={(tab) => setSelectedTab(tab as "emoji" | "upload")}
            >
              <Tabs.Group variant="responsive">
                <Tabs.Tab id="emoji">{i18n.t("stack_icon_emoji_tab")}</Tabs.Tab>
                <Tabs.Tab id="upload">
                  {i18n.t("stack_icon_upload_tab")}
                </Tabs.Tab>
              </Tabs.Group>
              <br />
              <Tabs.Panel tabId="emoji">
                <EmojiPicker onSelect={onEmojiSelect} />
              </Tabs.Panel>
              <Tabs.Panel tabId="upload">
                <StackUploadPanel
                  upload={updateStackLogo}
                  isUploading={isUploading}
                />
              </Tabs.Panel>
            </Tabs>
          </Popover.Body>
        </Popover.Content>
      </Popover.Overlay>
    </Popover>
  );
};
